import { EditableHeading, EditableImg, EditableText } from '@smartfire/components';
import { BlockWrapper, ChildrenBlock, defineBlock, smartProps, useInspectorControl } from '@smartfire/js-block-engine';
import Vimeo from '@u-wave/react-vimeo';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import YouTube from 'react-youtube';
import { ReactComponent as PlayButton } from '../assets/btn_play.svg';
import { Button } from '../button/block';
import style from './block.module.scss';
import Inspector from './inspector';

function youtube_parser(url) {
    var regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    var match = url.match(regExp);
    return match && match[7].length === 11 ? match[7] : false;
}

const CarouselVideos = ({ videos, image, imageTitle, imageSubtitle, anchorId }) => {
    useInspectorControl(Inspector);

    const [currentVideo, setCurrentVideo] = useState(0);
    const [vimeoPlaying, setVimeoPlaying] = useState(null);
    const isTablet = useMediaQuery({ query: '(max-width: ' + style.tabletBreakpoint + ')' });

    const goToVideo = (i) => {
        pauseVideo(currentVideo);
        setCurrentVideo(i);
    };

    const nextVideo = () => {
        pauseVideo(currentVideo);

        if (currentVideo >= videos.length - 1) {
            setCurrentVideo(0);
        } else {
            setCurrentVideo(currentVideo + 1);
        }
    };

    const dragNextVideo = () => {
        if (currentVideo < videos.length - 1) {
            pauseVideo(currentVideo);
            setCurrentVideo(currentVideo + 1);
        }
    };

    const dragPreviousVideo = () => {
        if (currentVideo > 0) {
            pauseVideo(currentVideo);
            setCurrentVideo(currentVideo - 1);
        }
    };

    const [isDragging, setIsDragging] = useState(false);
    const [previousDragX, setPreviousDragX] = useState(0);
    const [previousDragTime, setPreviousDragTime] = useState(0);

    const startDrag = (evt) => {
        if (evt.touches) {
            const touch = evt.touches[0] || evt.changedTouches[0];
            setPreviousDragX(touch.screenX);
        } else {
            evt.preventDefault();
            setPreviousDragX(evt.clientX);
        }
        setPreviousDragTime(0);
        setIsDragging(true);
    };

    const moveVideos = (x, timestamp) => {
        if (previousDragX !== 0 && timestamp > previousDragTime + 1000) {
            setPreviousDragTime(timestamp);
            if (previousDragX > x) {
                dragNextVideo();
            } else {
                dragPreviousVideo();
            }
            setPreviousDragX(x);
        }
    };

    const endDrag = () => {
        setIsDragging(false);
    };

    const handleTouchDrag = (evt) => {
        if (isDragging) {
            var touch = evt.touches[0] || evt.changedTouches[0];
            moveVideos(touch.screenX, evt.timeStamp);
        }
    };

    const handleDrag = (evt) => {
        if (isDragging && !isTablet) {
            evt.preventDefault();
            moveVideos(evt.clientX, evt.timeStamp);
        }
    };

    useEffect(() => {
        window.onmousemove = handleDrag;
        window.ontouchmove = handleTouchDrag;
        window.ontouchend = endDrag;
        window.ontouchcancel = endDrag;
        window.onmouseup = endDrag;
        window.onblur = endDrag;
    });

    const [videosStatus, setVideosStatus] = useState([]);
    useEffect(() => {
        setVideosStatus(
            videos.map((videos, i) => {
                return {
                    playing: false,
                };
            }),
        );
    }, [videos]);

    const playVideo = (i) => {
        if (videos[i].type === 'youtube') {
            refs.current[i].internalPlayer.playVideo();
        }
        if (videos[i].type === 'media') {
            refs.current[i].play();
        }
        if (videos[i].type === 'vimeo') {
            setVimeoPlaying(i);
        }
        var newStatus = structuredClone(videosStatus);
        newStatus[i].playing = true;
        setVideosStatus(newStatus);
    };

    const pauseVideo = (i) => {
        if (videos[i].type === 'youtube') {
            refs.current[i].internalPlayer.pauseVideo();
        }
        if (videos[i].type === 'media') {
            refs.current[i].pause();
        }
        var newStatus = structuredClone(videosStatus);
        newStatus[i].playing = false;
        setVideosStatus(newStatus);
    };

    const videoPaused = (i) => {
        if (videos[i].type === 'vimeo') {
            setVimeoPlaying(null);
        }

        var newStatus = structuredClone(videosStatus);
        newStatus[i].playing = false;
        setVideosStatus(newStatus);
    };

    const videoPlayed = (i) => {
        var newStatus = structuredClone(videosStatus);
        newStatus[i].playing = true;
        setVideosStatus(newStatus);
    };

    const refs = useRef([]);
    useEffect(() => {
        refs.current = refs.current.slice(0, videos.length);
    }, [videos]);

    const scrollRef = useRef(null);

    const displayButtons = () => {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
            >
                <ChildrenBlock
                    key="children"
                    gutenbergEditorConfig={{
                        orientation: 'vertical',
                        allowedBlocks: ['smartfire/button'],
                        template: [['smartfire/button', {}]],
                    }}
                    className={style.buttons}
                />
            </motion.div>
        );
    };

    return (
        <BlockWrapper.Div id={'anchor-' + anchorId} className={style.block}>
            <div className={style.content}>
                <EditableHeading
                    className={style.title}
                    smartProp="title"
                    placeholder="Titre..."
                    allowedFormats={['core/text-color', 'core/image']}
                />
                <div className={style.columns}>
                    <div className={style.left}>
                        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
                            <EditableText smartProp="text" tagName="p" placeholder="..." />
                        </motion.div>
                        <div className={clsx(style.image, !image && style.imageEdit)}>
                            <EditableImg smartProp={'image'} />
                            <div className={style.imageTitle}>
                                <p>
                                    <strong>{imageTitle}</strong>
                                </p>
                                <p>{imageSubtitle}</p>
                            </div>
                        </div>
                        {!isTablet && displayButtons()}
                    </div>
                    <div className={style.right}>
                        <div className={style.videos} onMouseDown={startDrag} onTouchStart={startDrag} ref={scrollRef}>
                            {videos.map((video, i) => {
                                return (
                                    <React.Fragment key={'fragment_video_' + i}>
                                        {video.url.length > 0 && (
                                            <div
                                                key={'video_' + i}
                                                className={clsx(
                                                    style.video,
                                                    videosStatus[i] && videosStatus[i].playing && style.playing,
                                                    currentVideo > i + 1 && style.morePreviousVideo,
                                                    currentVideo > i && style.previousVideo,
                                                    currentVideo < i && style.nextVideo,
                                                    currentVideo < i - 1 && style.moreNextVideo,
                                                )}
                                                onClick={() => playVideo(i)}
                                            >
                                                <motion.div
                                                    initial={i < 2 && { opacity: 0, y: 100 }} // Animate only the two first videos
                                                    whileInView={{ opacity: 1, y: 0 }}
                                                    viewport={{ once: true, root: scrollRef }}
                                                    transition={{ delay: 1 + i / 2 }}
                                                >
                                                    <PlayButton className={style.playBtn} />
                                                    {video.type === 'youtube' && video.url.length > 0 && (
                                                        <YouTube
                                                            videoId={youtube_parser(video.url)}
                                                            onPause={() => videoPaused(i)}
                                                            onPlay={() => videoPlayed(i)}
                                                            ref={(el) => (refs.current[i] = el)}
                                                        />
                                                    )}
                                                    {video.type === 'vimeo' && video.url.length > 0 && (
                                                        <Vimeo
                                                            video={video.url}
                                                            onPause={() => videoPaused(i)}
                                                            onPlay={() => videoPlayed(i)}
                                                            ref={(el) => (refs.current[i] = el)}
                                                            paused={vimeoPlaying !== i}
                                                            width="640"
                                                            height="360"
                                                        />
                                                    )}
                                                    {video.type === 'media' &&
                                                        video.id !== 0 &&
                                                        video.url.length > 0 && (
                                                            <video
                                                                controls
                                                                ref={(el) => (refs.current[i] = el)}
                                                                onPause={() => videoPaused(i)}
                                                                onPlay={() => videoPlayed(i)}
                                                            >
                                                                <source src={video.url} type="video/mp4" />
                                                            </video>
                                                        )}
                                                </motion.div>
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                        <div className={style.tracks}>
                            {videos.map((video, i) => {
                                return (
                                    <div
                                        key={'track_' + i}
                                        className={clsx(style.track, i === currentVideo && style.activeTrack)}
                                        onClick={() => goToVideo(i)}
                                    ></div>
                                );
                            })}
                        </div>
                        {isTablet && displayButtons()}
                        {!isTablet && (
                            <Button
                                className={style.videoButton}
                                tagName="a"
                                text={currentVideo === videos.length - 1 ? 'Retour' : 'Suivant'}
                                color={style.blue}
                                color2={style.yellow}
                                type={'borders'}
                                shape={'rounded'}
                                arrow={currentVideo === videos.length - 1 ? 'leftArrow' : 'rightArrow'}
                                onClick={nextVideo}
                                showInspector={false}
                            />
                        )}
                    </div>
                </div>
            </div>
        </BlockWrapper.Div>
    );
};

export default defineBlock({
    Block: CarouselVideos,
    smartProps: {
        title: smartProps.object.withDefault({
            headingLevel: 2,
            content: '',
        }),
        image: smartProps.image,
        imageTitle: smartProps.string.withDefault(''),
        imageSubtitle: smartProps.string.withDefault(''),
        text: smartProps.string.withDefault(''),
        videos: smartProps.array.withDefault([]),
        anchorId: smartProps.string.withDefault('nos-videos'),
    },
    setup: {
        title: 'Carousel Videos',
    },
});
